/**
 * ImpersonateModal - Modal for admin to select a user to impersonate
 * Supports company selection for users with multiple company memberships
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from './ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { API_BASE } from '../lib/apiBase';
import { 
    Search, 
    Eye, 
    Loader2, 
    User, 
    Building2, 
    Shield, 
    AlertTriangle,
    Users,
    Star
} from 'lucide-react';

const API = API_BASE;

export const ImpersonateModal = ({ open, onOpenChange }) => {
    const { startImpersonation } = useAuth();
    
    const [organizations, setOrganizations] = useState([]);
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    
    // Form state
    const [selectedOrgId, setSelectedOrgId] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedCompanyId, setSelectedCompanyId] = useState(''); // For multi-company users
    const [readOnly, setReadOnly] = useState(true);

    // Fetch organizations and users when modal opens
    useEffect(() => {
        if (open) {
            fetchData();
        } else {
            // Reset state when modal closes
            setSelectedUserId('');
            setSelectedCompanyId('');
            setSearchQuery('');
            setSelectedOrgId('all');
        }
    }, [open]);

    // Filter users based on org and search
    useEffect(() => {
        let filtered = users;
        
        if (selectedOrgId && selectedOrgId !== 'all') {
            filtered = filtered.filter(u => 
                u.organization_id === selectedOrgId ||
                u.companies?.some(c => c.id === selectedOrgId)
            );
        }
        
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(u => 
                u.name?.toLowerCase().includes(query) ||
                u.email?.toLowerCase().includes(query) ||
                u.organization_name?.toLowerCase().includes(query) ||
                u.companies?.some(c => c.name?.toLowerCase().includes(query))
            );
        }
        
        setFilteredUsers(filtered);
    }, [users, selectedOrgId, searchQuery]);

    // Reset company selection when user changes
    useEffect(() => {
        setSelectedCompanyId('');
    }, [selectedUserId]);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            
            // Fetch organizations
            const orgsResponse = await axios.get(`${API}/api/organizations`, { headers });
            setOrganizations(orgsResponse.data || []);
            
            // Fetch users available for impersonation (now includes companies data)
            const usersResponse = await axios.get(`${API}/api/admin/users-for-impersonation`, { headers });
            setUsers(usersResponse.data || []);
            setFilteredUsers(usersResponse.data || []);
        } catch (error) {
            toast.error('Failed to load users');
            console.error('Failed to fetch data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStartImpersonation = async () => {
        if (!selectedUserId) {
            toast.error('Please select a user');
            return;
        }
        
        const selectedUser = users.find(u => u.id === selectedUserId);
        
        // If user has multiple companies and none selected, require selection
        if (selectedUser?.has_multiple_companies && !selectedCompanyId) {
            toast.error('Please select a company context');
            return;
        }
        
        setIsStarting(true);
        try {
            // Pass company_id if selected (for multi-company users)
            await startImpersonation(selectedUserId, readOnly, selectedCompanyId || null);
            toast.success('Now viewing as selected user');
            onOpenChange(false);
            
            // Refresh the page to load user's view
            window.location.href = '/dashboard';
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start impersonation');
        } finally {
            setIsStarting(false);
        }
    };

    const getRoleBadge = (role) => {
        const config = {
            customer: { bg: 'bg-blue-50', color: 'text-blue-600', label: 'Customer' },
            repro_partner: { bg: 'bg-violet-50', color: 'text-violet-600', label: 'Partner' },
            translator: { bg: 'bg-purple-50', color: 'text-purple-600', label: 'Translator' },
        };
        const c = config[role] || { bg: 'bg-background', color: 'text-slate-600', label: role };
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.color}`}>
                {c.label}
            </span>
        );
    };

    const selectedUser = users.find(u => u.id === selectedUserId);
    const selectedCompany = selectedUser?.companies?.find(c => c.id === selectedCompanyId);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Eye className="h-5 w-5 text-amber-500" />
                        View as Another User
                    </DialogTitle>
                    <DialogDescription>
                        See the application exactly as a customer or partner would see it.
                        An audit log will be created for security purposes.
                    </DialogDescription>
                </DialogHeader>
                
                <div className="flex-1 overflow-hidden flex flex-col gap-4 py-4">
                    {/* Filters */}
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                    placeholder="Search by name or email..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9"
                                    data-testid="impersonate-search"
                                />
                            </div>
                        </div>
                        <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                            <SelectTrigger className="w-[200px]" data-testid="impersonate-org-filter">
                                <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                                <SelectValue placeholder="All Companies" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Companies</SelectItem>
                                {organizations.map((org) => (
                                    <SelectItem key={org.id} value={org.id}>
                                        {org.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* User List */}
                    <div className="flex-1 border rounded-lg overflow-hidden">
                        {isLoading ? (
                            <div className="flex items-center justify-center h-48 text-slate-400">
                                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                Loading users...
                            </div>
                        ) : filteredUsers.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                                <Users className="h-8 w-8 mb-2 opacity-50" />
                                <p>No users found</p>
                                {searchQuery && (
                                    <p className="text-sm">Try a different search term</p>
                                )}
                            </div>
                        ) : (
                            <div className="max-h-[220px] overflow-y-auto">
                                {filteredUsers.map((user) => (
                                    <div
                                        key={user.id}
                                        onClick={() => setSelectedUserId(user.id)}
                                        className={`flex items-center gap-3 p-3 cursor-pointer border-b last:border-b-0 transition-colors ${
                                            selectedUserId === user.id
                                                ? 'bg-amber-50 border-l-4 border-l-amber-500'
                                                : 'hover:bg-background'
                                        }`}
                                        data-testid={`user-option-${user.id}`}
                                    >
                                        <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-medium">
                                            {user.name?.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium text-slate-900 truncate">{user.name}</p>
                                                {getRoleBadge(user.role)}
                                                {user.has_multiple_companies && (
                                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-600">
                                                        {user.companies?.length} companies
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-500 truncate">{user.email}</p>
                                            {user.has_multiple_companies ? (
                                                <p className="text-xs text-slate-400 truncate flex items-center gap-1 mt-0.5">
                                                    <Building2 className="h-3 w-3" />
                                                    {user.companies?.map(c => c.name).join(', ')}
                                                </p>
                                            ) : user.organization_name && (
                                                <p className="text-xs text-slate-400 truncate flex items-center gap-1 mt-0.5">
                                                    <Building2 className="h-3 w-3" />
                                                    {user.organization_name}
                                                </p>
                                            )}
                                            {user.partner_name && (
                                                <p className="text-xs text-violet-500 truncate mt-0.5">
                                                    Partner: {user.partner_name}
                                                </p>
                                            )}
                                        </div>
                                        {selectedUserId === user.id && (
                                            <div className="text-amber-500">
                                                <Eye className="h-5 w-5" />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Company Selection for Multi-Company Users */}
                    {selectedUser?.has_multiple_companies && (
                        <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg space-y-2">
                            <Label className="text-sm font-medium text-indigo-800 flex items-center gap-2">
                                <Building2 className="h-4 w-4" />
                                Select Company Context
                            </Label>
                            <p className="text-xs text-indigo-600 mb-2">
                                This user belongs to multiple companies. Select which company context to view as.
                            </p>
                            <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                                <SelectTrigger className="bg-white" data-testid="impersonate-company-select">
                                    <SelectValue placeholder="Select a company..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {selectedUser.companies?.map((company) => (
                                        <SelectItem key={company.id} value={company.id}>
                                            <div className="flex items-center gap-2">
                                                <span>{company.name}</span>
                                                {company.is_primary && (
                                                    <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                                                )}
                                                <span className="text-xs text-slate-400 capitalize">({company.role})</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Read-only toggle */}
                    <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                        <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-slate-500" />
                            <div>
                                <p className="text-sm font-medium">Read-only mode</p>
                                <p className="text-xs text-slate-500">
                                    Prevents destructive actions while impersonating
                                </p>
                            </div>
                        </div>
                        <Switch
                            checked={readOnly}
                            onCheckedChange={setReadOnly}
                            data-testid="impersonate-readonly-toggle"
                        />
                    </div>

                    {/* Warning for non-read-only */}
                    {!readOnly && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                            <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                            <div className="text-red-700">
                                <strong>Warning:</strong> With read-only disabled, you can make changes 
                                on behalf of this user. All actions will be logged. Use with caution.
                            </div>
                        </div>
                    )}

                    {/* Selected user summary */}
                    {selectedUser && (
                        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <Eye className="h-5 w-5 text-amber-600" />
                            <div className="text-sm">
                                <span className="text-amber-800">Ready to view as: </span>
                                <strong className="text-amber-900">{selectedUser.name}</strong>
                                {selectedUser.has_multiple_companies ? (
                                    selectedCompany ? (
                                        <span className="text-amber-700"> @ {selectedCompany.name}</span>
                                    ) : (
                                        <span className="text-amber-600 italic"> (select company above)</span>
                                    )
                                ) : (
                                    selectedUser.organization_name && (
                                        <span className="text-amber-700"> @ {selectedUser.organization_name}</span>
                                    )
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleStartImpersonation}
                        disabled={!selectedUserId || isStarting || (selectedUser?.has_multiple_companies && !selectedCompanyId)}
                        className="bg-amber-500 hover:bg-amber-600"
                        data-testid="start-impersonation-btn"
                    >
                        {isStarting ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Starting...
                            </>
                        ) : (
                            <>
                                <Eye className="h-4 w-4 mr-2" />
                                Start Viewing
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ImpersonateModal;
